import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  MenuItem,
  Box,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Info as InfoIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  LocationCity as CityIcon,
  Map as MapIcon,
  Notes as NotesIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Container from '../../../components/layout/Container';
import Fieldset from '../../../components/Form/Fieldset';
import Row from '../../../components/Form/Row';
import Form from '../../../components/Form/Form';
import ZipCodeInput from '../../../components/inputs/ZipCodeInput';
import NumberInput from '../../../components/inputs/NumberInput';
import CountryInput from '../../../components/inputs/CountryInput';
import AppBarComponent from '../../../components/AppBarComponent';

// Estilos modernizados
const ButtonAreaStyled = styled(Box)`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const StyledFieldset = styled(Fieldset)`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
  }
`;

const SectionTitle = styled(Typography)`
  color: #424242;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
`;

const FormTitle = styled(Typography)`
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 24px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const clientTypes = ['INDIVIDUAL', 'COMPANY'];

const buildApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    console.error('API_BASE_URL não está definido no arquivo .env');
    return endpoint;
  }
  const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
  return new URL(endpoint, normalizedBase).toString();
};

export default function ClientUpdatePage() {
  const { email } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    reset,
    setValue,
    watch
  } = useForm({
    mode: 'onChange'
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(true);

  // Carrega os dados do cliente
  useEffect(() => {
    const loadClientData = async () => {
      try {
        // Verifica se há dados no estado de navegação
        if (location.state?.clienteData) {
          const clientData = location.state.clienteData;
          setFormData(clientData);
          setIsLoading(false);
          return;
        }

        // Se não houver dados no estado, busca da API
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.token) {
          throw new Error('Usuário não autenticado');
        }

        const response = await axios.get(
          buildApiUrl(`customers/${email}`),
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        if (response.status === 200) {
          const clientData = response.data;
          setFormData(clientData);
        }
      } catch (error) {
        console.error('Erro ao carregar cliente:', error);
        setSnackbarMessage('Erro ao carregar dados do cliente');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        navigate('/realocation/client-management');
      } finally {
        setIsLoading(false);
      }
    };

    const setFormData = (clientData) => {
      reset({
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone || '',
        clientType: clientData.clientType,
        observation: clientData.observation || '',
        address: clientData.address?.street || '',
        number: clientData.address?.number || '',
        complement: clientData.address?.complement || '',
        city: clientData.address?.city || '',
        state: clientData.address?.state || '',
        country: clientData.address?.country === 'BRAZIL' ? 'Brasil' : clientData.address?.country || '',
        zipCode: clientData.address?.zipCode || ''
      });
    };

    loadClientData();
  }, [email, reset, navigate, location.state]);

  // Preenchimento automático de endereço via CEP
  const zipCode = watch('zipCode');
  useEffect(() => {
    const fetchAddress = async () => {
      if (zipCode?.replace(/\D/g, '').length === 8) {
        try {
          const response = await axios.get(`https://viacep.com.br/ws/${zipCode}/json/`);
          const data = response.data;
          
          if (!data.erro) {
            setValue('address', data.logradouro || '', { shouldValidate: true });
            setValue('city', data.localidade || '', { shouldValidate: true });
            setValue('state', data.uf || '', { shouldValidate: true });
          }
        } catch (error) {
          console.error('Erro ao buscar CEP:', error);
        }
      }
    };

    fetchAddress();
  }, [zipCode, setValue]);

  const onSubmit = async (data) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.replace(/\D/g, ''),
        address: {
          street: data.address.trim(),
          number: data.number,
          complement: data.complement.trim(),
          neighborhood: '',
          city: data.city.trim(),
          state: data.state.trim().toUpperCase(),
          country: data.country === 'Brasil' ? 'BRAZIL' : data.country.toUpperCase(),
          zipCode: data.zipCode.replace(/\D/g, '')
        },
        clientType: data.clientType,
        observation: data.observation?.trim() || null
      };

      const response = await axios.put(
        buildApiUrl(`customers/${email}`),
        payload,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        setSnackbarMessage('Cliente atualizado com sucesso!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setTimeout(() => navigate('/realocation/client-management'), 1500);
      }
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      let errorMessage = 'Erro ao atualizar cliente';
      
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handlePhoneChange = (e, onChange) => {
    let value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    }
    if (value.length > 10) {
      value = `${value.substring(0, 10)}-${value.substring(10)}`;
    }
    
    onChange(value);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const observationLength = watch('observation')?.length || 0;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBarComponent 
        title="Edição de Cliente" 
        leftIcon={<ArrowBackIcon />} 
        onLeftIconClick={() => navigate('/realocation/client-management')}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <FormTitle variant="h1">
            <BusinessIcon fontSize="large" />
            Editar Cliente
          </FormTitle>
          
          <Typography variant="body1" color="text.secondary" mb={4}>
            Atualize os dados do cliente abaixo. Todos os campos marcados com * são obrigatórios.
          </Typography>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledFieldset>
              <SectionTitle variant="h2">
                <PersonIcon color="primary" />
                Informações Básicas
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ 
                    required: 'Nome é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Nome deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome Completo *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: João da Silva"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  rules={{ 
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Email *"
                      type="email"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: joao@empresa.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={3}>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ 
                    required: 'Telefone é obrigatório',
                    minLength: {
                      value: 14,
                      message: 'Telefone inválido'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Telefone *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Ex: (99) 99999-9999'}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => handlePhoneChange(e, field.onChange)}
                      inputProps={{
                        maxLength: 15
                      }}
                      placeholder="(00) 00000-0000"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="clientType"
                  control={control}
                  rules={{ required: 'Tipo de cliente é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Cliente *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {clientTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type === 'INDIVIDUAL' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <HomeIcon color="primary" />
                Endereço
                <Tooltip title="Informações completas do endereço para entrega e faturamento">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="address"
                  control={control}
                  rules={{ 
                    required: 'Endereço é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Endereço deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Endereço *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Rua das Flores, 123"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <NumberInput
                  control={control}
                  name="number"
                  label="Número *"
                  required
                  min={1}
                  error={!!errors.number}
                  helperText={errors.number?.message}
                  placeholder="Ex: 123"
                  startAdornment={<HomeIcon color={errors.number ? 'error' : 'action'} />}
                />
              </Row>

              <Row columns={3}>
                <ZipCodeInput
                  control={control}
                  name="zipCode"
                  label="CEP *"
                  required
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message || 'Ex: 00000-000'}
                  placeholder="00000-000"
                  startAdornment={<MapIcon color={errors.zipCode ? 'error' : 'action'} />}
                />
                <Controller
                  name="city"
                  control={control}
                  rules={{ 
                    required: 'Cidade é obrigatória',
                    minLength: {
                      value: 3,
                      message: 'Cidade deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cidade *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: São Paulo"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="state"
                  control={control}
                  rules={{ 
                    required: 'Estado é obrigatório',
                    minLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    },
                    maxLength: {
                      value: 2,
                      message: 'Estado deve ter 2 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Estado (UF) *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Ex: SP'}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: 2,
                        style: { textTransform: 'uppercase' }
                      }}
                      placeholder="SP"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={1}>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: 'País é obrigatório' }}
                  render={({ fieldState }) => (
                    <CountryInput
                      control={control}
                      name="country"
                      label="País *"
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      startAdornment={<MapIcon color={fieldState.error ? 'error' : 'action'} />}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <NotesIcon color="primary" />
                Observações
                <Tooltip title="Informações adicionais sobre o cliente">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              <Row columns={1}>
                <Controller
                  name="observation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Observações Adicionais"
                      multiline
                      rows={4}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        maxLength: 500
                      }}
                      placeholder="Informações relevantes sobre o cliente (opcional)"
                      helperText={`${observationLength}/500 caracteres`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <NotesIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <ButtonAreaStyled>
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate('/realocation/client-management')}
                sx={{ 
                  minWidth: 120,
                  px: 3,
                  py: 1,
                  borderRadius: 2
                }}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !isValid}
                sx={{ 
                  minWidth: 200,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
                startIcon={<SaveIcon />}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            </ButtonAreaStyled>
          </Form>
        </Paper>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}